import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Pagination,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  Card,
  Chip,
  Avatar,
  Stack,
  Tabs,
  Tab,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Menu,
} from '@mui/material';
import {
  Refresh,
  Search,
  FilterList,
  KeyboardArrowDown,
  KeyboardArrowUp,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Person,
  Email,
  Phone,
  CalendarToday,
  CheckCircle,
  Cancel,
  Close,
  Send,
  Key,
  Download,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';
import {
  useListUsersQuery,
  useSendCouponMutation,
} from '../../features/admin/userlistApi.ts';
import {
  useListCouponsQuery,
  Coupon,
} from '../../features/admin/couponApi.ts';

interface User {
  user_id: number;
  first_name: string;
  last_name: string;
  user_email: string;
  user_contact: string;
  otp: number | string;
  status: string;
  member_type: string;
  subscription_date: string | null;
  expiry_date: string | null;
}

interface UserCouponHistory {
  [userId: number]: number[];
}

const normalizeContactNumber = (contact: string | undefined): string => {
  if (!contact) return '';
  return contact.replace(/\D/g, '');
};

// ── Column definitions ──────────────────────────────────────────────────────
const COL_LABELS  = ['', 'ID', 'User', 'OTP', 'Email', 'Contact', 'Status', 'Membership', 'Subscribed', 'Expires'];
const COL_WIDTHS  = [36, 64, 150, 100, 180, 120, 95, 105, 105, 105];
const GRID_COLS   = COL_WIDTHS.map(w => `${w}px`).join(' ');
const TABLE_MIN_W = COL_WIDTHS.reduce((a, b) => a + b, 0) + COL_WIDTHS.length * 16;

const UserList = () => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen]                     = useState(false);
  const [page, setPage]                                 = useState(1);
  const [searchStr, setSearchStr]                       = useState('');
  const [tabValue, setTabValue]                         = useState<number>(0);
  const [snackbar, setSnackbar]                         = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' });
  const [expandedUserId, setExpandedUserId]             = useState<number | null>(null);
  const [openSendCouponDialog, setOpenSendCouponDialog] = useState(false);
  const [selectedUsers, setSelectedUsers]               = useState<number[]>([]);
  const [selectedCoupon, setSelectedCoupon]             = useState<number | ''>('');
  const [isSending, setIsSending]                       = useState(false);
  const [userCouponHistory, setUserCouponHistory]       = useState<UserCouponHistory>({});
  const [downloadAnchorEl, setDownloadAnchorEl]         = useState<null | HTMLElement>(null);
  const [apiErrors, setApiErrors]                       = useState<string[]>([]);

  // ── Scroll state for left/right arrows ──────────────────────────────────
  const scrollRef                     = useRef<HTMLDivElement>(null);
  const [canScrollLeft,  setCanScrollLeft]  = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', updateScrollState); ro.disconnect(); };
  }, [updateScrollState]);

  const scrollBy = (dir: 'left' | 'right') => {
    scrollRef.current?.scrollBy({ left: dir === 'left' ? -200 : 200, behavior: 'smooth' });
  };

  const [sendCoupon] = useSendCouponMutation();

  const { data: dataNormal,   isLoading: loadingNormal,   refetch: refetchNormal,   error: errorNormal,   isError: isErrorNormal   } = useListUsersQuery({ filter_type: 0 });
  const { data: dataSilver,   isLoading: loadingSilver,   refetch: refetchSilver,   error: errorSilver,   isError: isErrorSilver   } = useListUsersQuery({ filter_type: 1 });
  const { data: dataGold,     isLoading: loadingGold,     refetch: refetchGold,     error: errorGold,     isError: isErrorGold     } = useListUsersQuery({ filter_type: 2 });
  const { data: dataPlatinum, isLoading: loadingPlatinum, refetch: refetchPlatinum, error: errorPlatinum, isError: isErrorPlatinum } = useListUsersQuery({ filter_type: 3 });
  const { data: couponsData,  isLoading: isLoadingCoupons }                                                                          = useListCouponsQuery();

  useEffect(() => {
    const errors: string[] = [];
    const check = (queryData: any, isError: boolean, errorObj: any, label: string) => {
      if (isError && errorObj) {
        errors.push(`${label}: ${(errorObj as any)?.data?.message || (errorObj as any)?.error || `Failed to load ${label} users`}`);
      } else if (queryData?.error) {
        errors.push(`${label}: ${queryData.message || 'Unknown error'}`);
      }
    };
    check(dataNormal,   isErrorNormal,   errorNormal,   'Normal');
    check(dataSilver,   isErrorSilver,   errorSilver,   'Silver');
    check(dataGold,     isErrorGold,     errorGold,     'Gold');
    check(dataPlatinum, isErrorPlatinum, errorPlatinum, 'Platinum');
    setApiErrors(errors);
  }, [dataNormal, dataSilver, dataGold, dataPlatinum, isErrorNormal, isErrorSilver, isErrorGold, isErrorPlatinum, errorNormal, errorSilver, errorGold, errorPlatinum]);

  const usersNormal:   User[] = dataNormal?.data   || [];
  const usersSilver:   User[] = dataSilver?.data   || [];
  const usersGold:     User[] = dataGold?.data     || [];
  const usersPlatinum: User[] = dataPlatinum?.data || [];
  const allCoupons             = couponsData?.data  || [];

  const filterCounts = { normal: usersNormal.length, silver: usersSilver.length, gold: usersGold.length, platinum: usersPlatinum.length };

  const activeUsers: User[] = useMemo(() => {
    switch (tabValue) {
      case 1: return usersSilver;
      case 2: return usersGold;
      case 3: return usersPlatinum;
      default: return usersNormal;
    }
  }, [tabValue, usersNormal, usersSilver, usersGold, usersPlatinum]);

  const isLoading =
    (tabValue === 0 && loadingNormal) || (tabValue === 1 && loadingSilver) ||
    (tabValue === 2 && loadingGold)   || (tabValue === 3 && loadingPlatinum);

  const allUsers: User[] = useMemo(
    () => [...usersNormal, ...usersSilver, ...usersGold, ...usersPlatinum],
    [usersNormal, usersSilver, usersGold, usersPlatinum]
  );

  const usersPerPage = 10;

  const filteredUsers: User[] = useMemo(() => {
    if (!searchStr.trim()) return activeUsers;
    const s  = searchStr.trim().toLowerCase();
    const ns = normalizeContactNumber(s);
    return activeUsers.filter((u: User) => {
      const full = `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase().trim();
      const nc   = normalizeContactNumber(u.user_contact);
      return (
        (u.first_name?.toLowerCase().includes(s)  ?? false) ||
        (u.last_name?.toLowerCase().includes(s)   ?? false) ||
        full.includes(s) ||
        (u.user_email?.toLowerCase().includes(s)  ?? false) ||
        nc.includes(ns) ||
        (u.user_contact?.includes(s)              ?? false) ||
        (u.user_id?.toString().includes(s)        ?? false) ||
        (u.otp?.toString().includes(s)            ?? false)
      );
    });
  }, [activeUsers, searchStr]);

  const activeCoupons = useMemo(() => {
    const now = new Date();
    return allCoupons.filter((c: Coupon) => !(new Date(c.end_date) < now) && c.status !== '1');
  }, [allCoupons]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / usersPerPage));
  useEffect(() => { if (page > totalPages) setPage(1); }, [filteredUsers, totalPages, page]);

  const currentPage    = Math.min(page, totalPages);
  const paginatedUsers = filteredUsers.slice((currentPage - 1) * usersPerPage, currentPage * usersPerPage);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleRefresh = async () => {
    try {
      await Promise.all([refetchNormal(), refetchSilver(), refetchGold(), refetchPlatinum()]);
      setPage(1);
      setSnackbar({ open: true, message: 'Users refreshed successfully!', severity: 'success' });
    } catch { setSnackbar({ open: true, message: 'Error refreshing users', severity: 'error' }); }
  };

  const handleTabChange     = (_: React.SyntheticEvent, v: number) => { setTabValue(v); setPage(1); setSearchStr(''); };
  const handleSearch        = (e: React.ChangeEvent<HTMLInputElement>) => { setSearchStr(e.target.value); setPage(1); };
  const clearFilters        = () => { setSearchStr(''); setPage(1); };
  const handleDrawerToggle  = () => setMobileOpen(!mobileOpen);
  const handleChangePage    = (_: React.ChangeEvent<unknown>, p: number) => setPage(p);
  const handleCloseSnackbar = () => setSnackbar(s => ({ ...s, open: false }));
  const handleToggleExpand  = (id: number) => setExpandedUserId(prev => prev === id ? null : id);

  // ── CSV ───────────────────────────────────────────────────────────────────

  const handleDownloadClick = (e: React.MouseEvent<HTMLElement>) => setDownloadAnchorEl(e.currentTarget);
  const handleDownloadClose = () => setDownloadAnchorEl(null);

  const downloadCSV = (list: User[], filename: string) => {
    const hdr  = ['User ID','First Name','Last Name','Email','Contact','OTP','Status','Member Type','Subscription Date','Expiry Date'];
    const rows = list.map(u =>
      [u.user_id,`"${u.first_name||''}"`,`"${u.last_name||''}"`,`"${u.user_email||''}"`,`"${u.user_contact||''}"`,
       u.otp||'',u.status||'',u.member_type||'None',u.subscription_date||'',u.expiry_date||''].join(',')
    );
    const csv  = [hdr.join(','), ...rows].join('\n');
    const link = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' })),
      download: filename, style: 'visibility:hidden',
    });
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
    setSnackbar({ open: true, message: 'CSV downloaded!', severity: 'success' });
  };

  const tabLabel             = ['normal','silver','gold','platinum'][tabValue];
  const today                = new Date().toISOString().split('T')[0];
  const downloadAllUsersCSV      = () => { downloadCSV(allUsers,       `all_users_${today}.csv`);                  handleDownloadClose(); };
  const downloadFilteredUsersCSV = () => { downloadCSV(filteredUsers,  `${tabLabel}_users_${today}.csv`);          handleDownloadClose(); };
  const downloadCurrentPageCSV   = () => { downloadCSV(paginatedUsers, `users_page_${currentPage}_${today}.csv`);  handleDownloadClose(); };

  // ── Send Coupon ───────────────────────────────────────────────────────────

  const handleOpenSendCouponDialog  = () => { setOpenSendCouponDialog(true);  setSelectedUsers([]); setSelectedCoupon(''); setUserCouponHistory({}); };
  const handleCloseSendCouponDialog = () => { setOpenSendCouponDialog(false); setSelectedUsers([]); setSelectedCoupon(''); setUserCouponHistory({}); };

  const handleUserSelection  = (id: number) =>
    setSelectedUsers(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const handleSelectAllUsers = () =>
    setSelectedUsers(selectedUsers.length === filteredUsers.length ? [] : filteredUsers.map(u => u.user_id));

  const handleSendCoupon = async () => {
    if (!selectedCoupon || selectedUsers.length === 0) {
      setSnackbar({ open: true, message: 'Please select a coupon and at least one user', severity: 'error' }); return;
    }
    setIsSending(true);
    let ok = 0, already = 0, fail = 0;
    try {
      for (const userId of selectedUsers) {
        try {
          const res = await sendCoupon({ coupon_id: Number(selectedCoupon), user_id: userId }).unwrap();
          if (res.sent_users === 1)              { ok++;     setUserCouponHistory(p => ({ ...p, [userId]: [...(p[userId]||[]), Number(selectedCoupon)] })); }
          else if (res.already_sent_users === 1) { already++; setUserCouponHistory(p => ({ ...p, [userId]: [...(p[userId]||[]), Number(selectedCoupon)] })); }
          else { fail++; }
        } catch { fail++; }
      }
      if (ok > 0 && already === 0)    { setSnackbar({ open: true, message: `Coupon sent to ${ok} user${ok > 1 ? 's' : ''}!`, severity: 'success' }); handleCloseSendCouponDialog(); }
      else if (ok > 0 && already > 0) { setSnackbar({ open: true, message: `Sent to ${ok}. ${already} already received.`, severity: 'warning' }); }
      else if (already > 0)           { setSnackbar({ open: true, message: 'Coupon already sent to selected user(s).', severity: 'error' }); }
      else                            { setSnackbar({ open: true, message: 'Failed to send coupon. Try again.', severity: 'error' }); }
    } finally { setIsSending(false); }
  };

  const hasUserReceivedCoupon = (id: number) => userCouponHistory[id]?.includes(Number(selectedCoupon)) || false;

  // ── Helpers ───────────────────────────────────────────────────────────────

  const formatDate = (d: string | null) =>
    d ? new Date(d).toLocaleString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }) : '-';

  const getMemberTypeColor = (t: string) => {
    switch (t?.toLowerCase()) {
      case 'silver':   return { bgcolor: '#e8e6fa', color: '#3f51b5' };
      case 'gold':     return { bgcolor: '#fff8e1', color: '#f57c00' };
      case 'platinum': return { bgcolor: '#f3e5f5', color: '#7b1fa2' };
      default:         return { bgcolor: '#f5f5f5', color: '#616161' };
    }
  };

  const getStatusColor = (s: string) =>
    s?.toLowerCase() === 'active' ? { bgcolor: '#d1f4e0', color: '#00783e' } : { bgcolor: '#ffebee', color: '#c62828' };

  const getCouponText = (c: Coupon) =>
    `${c.code} - ${c.discount_type === 'flat' ? `AED ${c.discount_value}` : `${c.discount_value}%`}`;

  const formatOTP = (otp: number | string) => (!otp && otp !== 0 ? '-' : otp.toString());

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: '#f1f2f4' }}>

      {/* TOP HEADER */}
      <Box sx={{ flexShrink: 0, zIndex: 1200 }}>
        <AdminHeader onToggleMenu={handleDrawerToggle} />
      </Box>

      {/* BODY ROW */}
      <Box sx={{ flexGrow: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>

        {/* Sidebar desktop */}
        <Box sx={{ width: 250, flexShrink: 0, display: { xs: 'none', md: 'flex' }, flexDirection: 'column', overflow: 'hidden' }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>

        {/* Sidebar mobile */}
        <Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 250 } }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        {/* MAIN CONTENT */}
        <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>

          {/* Page header */}
          <Box sx={{ flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, pt: 3, pb: 2, bgcolor: '#f1f2f4' }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>User Management</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" startIcon={<Refresh />} onClick={handleRefresh} disabled={isLoading}
                sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px', fontWeight: 500 }}
              >Refresh</Button>
              <Button variant="outlined" startIcon={<Download />} onClick={handleDownloadClick}
                sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px', fontWeight: 500 }}
              >Download CSV</Button>
              <Button variant="contained" startIcon={<Send />} onClick={handleOpenSendCouponDialog}
                sx={{ bgcolor: '#008060', color: 'white', textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 2.5, '&:hover': { bgcolor: '#006e52' } }}
              >Send Coupon</Button>
            </Box>
          </Box>

          {/* API errors */}
          {apiErrors.length > 0 && (
            <Alert severity="error" sx={{ mx: 3, mb: 2, flexShrink: 0 }}
              action={<Button color="inherit" size="small" onClick={handleRefresh}>Retry</Button>}
            >
              <Typography variant="subtitle2">Failed to load user data</Typography>
              {apiErrors.map((err, i) => <Typography key={i} variant="body2">• {err}</Typography>)}
            </Alert>
          )}

          {/* Download menu */}
          <Menu anchorEl={downloadAnchorEl} open={Boolean(downloadAnchorEl)} onClose={handleDownloadClose}
            PaperProps={{ sx: { borderRadius: '8px', mt: 1, minWidth: 220 } }}
          >
            <MenuItem onClick={downloadAllUsersCSV}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2" fontWeight={500}>All Users ({allUsers.length})</Typography>
                <Typography variant="caption" color="text.secondary">Download complete user list</Typography>
              </Box>
            </MenuItem>
            <MenuItem onClick={downloadFilteredUsersCSV}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2" fontWeight={500}>
                  {tabLabel.charAt(0).toUpperCase() + tabLabel.slice(1)} ({filteredUsers.length})
                </Typography>
                <Typography variant="caption" color="text.secondary">Download current tab's users</Typography>
              </Box>
            </MenuItem>
            <MenuItem onClick={downloadCurrentPageCSV}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2" fontWeight={500}>Current Page ({paginatedUsers.length})</Typography>
                <Typography variant="caption" color="text.secondary">Download page {currentPage} only</Typography>
              </Box>
            </MenuItem>
          </Menu>

          {/* TABLE CARD */}
          <Card
            sx={{
              flexGrow: 1, mx: 3, mb: 3, minHeight: 0,
              borderRadius: '16px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              border: '1px solid #e1e3e5',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Frozen: Tabs */}
            <Box sx={{ flexShrink: 0, borderBottom: '1px solid #e1e3e5' }}>
              <Tabs value={tabValue} onChange={handleTabChange}
                sx={{
                  px: 2,
                  '& .MuiTab-root':       { textTransform: 'none', fontWeight: 500, fontSize: '14px', minHeight: 48, color: '#6d7175' },
                  '& .Mui-selected':      { color: '#202223', fontWeight: 600 },
                  '& .MuiTabs-indicator': { backgroundColor: '#202223', height: 3 },
                }}
              >
                <Tab value={0} label={loadingNormal   ? <Box sx={{ display:'flex',alignItems:'center',gap:.5 }}>Normal   <CircularProgress size={12}/></Box> : `Normal (${filterCounts.normal})`}    />
                <Tab value={1} label={loadingSilver   ? <Box sx={{ display:'flex',alignItems:'center',gap:.5 }}>Silver   <CircularProgress size={12}/></Box> : `Silver (${filterCounts.silver})`}    />
                <Tab value={2} label={loadingGold     ? <Box sx={{ display:'flex',alignItems:'center',gap:.5 }}>Gold     <CircularProgress size={12}/></Box> : `Gold (${filterCounts.gold})`}        />
                <Tab value={3} label={loadingPlatinum ? <Box sx={{ display:'flex',alignItems:'center',gap:.5 }}>Platinum <CircularProgress size={12}/></Box> : `Platinum (${filterCounts.platinum})`} />
              </Tabs>
            </Box>

            {/* Frozen: Search */}
            <Box sx={{ flexShrink: 0, p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1 }}>
              <TextField size="small"
                placeholder="Search users by name, email, contact, ID or OTP..."
                value={searchStr} onChange={handleSearch} fullWidth
                sx={{ maxWidth: 440, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" sx={{ color: '#6d7175' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button variant="outlined" startIcon={<FilterList />} onClick={clearFilters}
                sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px', fontWeight: 500 }}
              >Clear</Button>
            </Box>

            {/* ══════════════════════════════════════════════════════════════
                SCROLL WRAPPER — position:relative so the arrow buttons and
                fade shadows can be absolutely positioned at the corners.
                ══════════════════════════════════════════════════════════════ */}
            <Box sx={{ flexGrow: 1, position: 'relative', minHeight: 0, overflow: 'hidden' }}>

              {/* LEFT fade shadow — visible when content is scrolled right */}
              <Box
                sx={{
                  pointerEvents: 'none',
                  position: 'absolute', top: 0, left: 0, bottom: 0, width: 48,
                  background: 'linear-gradient(to right, rgba(255,255,255,0.92), transparent)',
                  zIndex: 3,
                  opacity: canScrollLeft ? 1 : 0,
                  transition: 'opacity 0.2s ease',
                }}
              />

              {/* RIGHT fade shadow — visible when more content exists to the right */}
              <Box
                sx={{
                  pointerEvents: 'none',
                  position: 'absolute', top: 0, right: 0, bottom: 0, width: 48,
                  background: 'linear-gradient(to left, rgba(255,255,255,0.92), transparent)',
                  zIndex: 3,
                  opacity: canScrollRight ? 1 : 0,
                  transition: 'opacity 0.2s ease',
                }}
              />

              {/* LEFT arrow — bottom-left corner */}
              {canScrollLeft && (
                <IconButton
                  size="small"
                  onClick={() => scrollBy('left')}
                  sx={{
                    position: 'absolute',
                    bottom: 10,
                    left: 6,
                    zIndex: 4,
                    bgcolor: 'white',
                    border: '1px solid #e1e3e5',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                    width: 28,
                    height: 28,
                    '&:hover': { bgcolor: '#f5f5f5' },
                  }}
                >
                  <KeyboardArrowLeft fontSize="small" sx={{ color: '#202223' }} />
                </IconButton>
              )}

              {/* RIGHT arrow — bottom-right corner */}
              {canScrollRight && (
                <IconButton
                  size="small"
                  onClick={() => scrollBy('right')}
                  sx={{
                    position: 'absolute',
                    bottom: 10,
                    right: 6,
                    zIndex: 4,
                    bgcolor: 'white',
                    border: '1px solid #e1e3e5',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                    width: 28,
                    height: 28,
                    '&:hover': { bgcolor: '#f5f5f5' },
                  }}
                >
                  <KeyboardArrowRight fontSize="small" sx={{ color: '#202223' }} />
                </IconButton>
              )}

              {/* ACTUAL SCROLL CONTAINER — ref attached here */}
              <Box
                ref={scrollRef}
                sx={{
                  height: '100%',
                  overflow: 'auto',
                  '&::-webkit-scrollbar':            { width: '6px', height: '6px' },
                  '&::-webkit-scrollbar-track':       { background: '#f1f1f1' },
                  '&::-webkit-scrollbar-thumb':       { background: '#c1c1c1', borderRadius: '4px' },
                  '&::-webkit-scrollbar-thumb:hover': { background: '#a8a8a8' },
                }}
              >
                {/* Inner wrapper — enforces minimum table width */}
                <Box sx={{ minWidth: TABLE_MIN_W, position: 'relative' }}>

                  {/* STICKY COLUMN HEADERS */}
                  {!isMobile && (
                    <Box
                      sx={{
                        position: 'sticky', top: 0, zIndex: 2,
                        display: 'grid', gridTemplateColumns: GRID_COLS,
                        gap: 2, px: 2, py: 1.5,
                        bgcolor: '#fafbfb',
                        borderBottom: '2px solid #e1e3e5',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                      }}
                    >
                      {COL_LABELS.map((h, i) => (
                        <Typography key={i} variant="caption"
                          sx={{ color: '#6d7175', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}
                        >
                          {h}
                        </Typography>
                      ))}
                    </Box>
                  )}

                  {/* Loading */}
                  {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                      <CircularProgress size={32} sx={{ color: '#008060' }} />
                    </Box>
                  )}

                  {/* Empty state */}
                  {!isLoading && paginatedUsers.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                      <Person sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>No users found</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {searchStr ? `No results for "${searchStr}"` : 'No users in this category'}
                      </Typography>
                      {searchStr && (
                        <Button variant="outlined" onClick={clearFilters}
                          sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px', fontWeight: 500 }}
                        >Clear Search</Button>
                      )}
                    </Box>
                  )}

                  {/* Search result count */}
                  {!isLoading && paginatedUsers.length > 0 && searchStr && (
                    <Box sx={{ px: 2, py: 1.5, bgcolor: '#fafbfb', borderBottom: '1px solid #e1e3e5' }}>
                      <Typography variant="body2" color="text.secondary">
                        Found {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} for "{searchStr}"
                      </Typography>
                    </Box>
                  )}

                  {/* DATA ROWS */}
                  {!isLoading && paginatedUsers.length > 0 && (
                    <Box sx={{ bgcolor: 'white' }}>
                      {paginatedUsers.map((user: User) => (
                        <Box key={user.user_id}>
                          <Box
                            onClick={() => handleToggleExpand(user.user_id)}
                            sx={
                              !isMobile
                                ? {
                                    display: 'grid', gridTemplateColumns: GRID_COLS,
                                    gap: 2, px: 2, py: 1.8, alignItems: 'center',
                                    borderBottom: '1px solid #f0f0f0',
                                    '&:hover': { bgcolor: '#f9fafb' },
                                    transition: 'background-color 0.12s ease',
                                    cursor: 'pointer',
                                  }
                                : {
                                    p: 2, borderBottom: '1px solid #e1e3e5',
                                    '&:hover': { bgcolor: '#f9fafb' }, cursor: 'pointer',
                                  }
                            }
                          >
                            {!isMobile ? (
                              <>
                                <IconButton size="small"
                                  onClick={e => { e.stopPropagation(); handleToggleExpand(user.user_id); }}
                                  sx={{ color: '#6d7175' }}
                                >
                                  {expandedUserId === user.user_id ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                                </IconButton>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223', fontSize: '12px' }}>#{user.user_id}</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                                  <Avatar sx={{ width: 28, height: 28, flexShrink: 0, bgcolor: '#f1f2f4', color: '#202223', fontSize: '12px', fontWeight: 600 }}>
                                    {(user.first_name || 'U')[0].toUpperCase()}
                                  </Avatar>
                                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {`${user.first_name || ''} ${user.last_name || ''}`.trim() || 'N/A'}
                                  </Typography>
                                </Box>
                                <Tooltip title="OTP" arrow>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Key sx={{ fontSize: 12, color: '#6d7175' }} />
                                    <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', color: '#202223', fontSize: '12px' }}>
                                      {formatOTP(user.otp)}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                                <Typography variant="body2" color="text.secondary"
                                  sx={{ fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                  title={user.user_email}
                                >
                                  {user.user_email || '-'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '11px' }}>
                                  {user.user_contact || '-'}
                                </Typography>
                                <Chip label={user.status || 'Unknown'} size="small"
                                  icon={user.status?.toLowerCase() === 'active' ? <CheckCircle sx={{ fontSize: 12 }} /> : <Cancel sx={{ fontSize: 12 }} />}
                                  sx={{ ...getStatusColor(user.status), fontWeight: 600, fontSize: '10px', height: 24 }}
                                />
                                <Chip label={user.member_type || 'None'} size="small"
                                  sx={{ ...getMemberTypeColor(user.member_type), fontWeight: 600, fontSize: '10px', height: 24 }}
                                />
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '11px' }}>
                                  {formatDate(user.subscription_date)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '11px' }}>
                                  {formatDate(user.expiry_date)}
                                </Typography>
                              </>
                            ) : (
                              <Stack spacing={1.5}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar sx={{ width: 38, height: 38, bgcolor: '#f1f2f4', color: '#202223', fontWeight: 600 }}>
                                      {(user.first_name || 'U')[0].toUpperCase()}
                                    </Avatar>
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>
                                        {`${user.first_name || ''} ${user.last_name || ''}`.trim() || 'N/A'}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">ID: #{user.user_id}</Typography>
                                    </Box>
                                  </Box>
                                  <IconButton size="small" onClick={e => { e.stopPropagation(); handleToggleExpand(user.user_id); }} sx={{ color: '#6d7175' }}>
                                    {expandedUserId === user.user_id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                  </IconButton>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Key sx={{ fontSize: 14, color: '#6d7175' }} />
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>OTP: {formatOTP(user.otp)}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                  <Chip label={user.status || 'Unknown'} size="small"
                                    icon={user.status?.toLowerCase() === 'active' ? <CheckCircle sx={{ fontSize: 14 }} /> : <Cancel sx={{ fontSize: 14 }} />}
                                    sx={{ ...getStatusColor(user.status), fontWeight: 600 }}
                                  />
                                  <Chip label={user.member_type || 'None'} size="small"
                                    sx={{ ...getMemberTypeColor(user.member_type), fontWeight: 600 }}
                                  />
                                </Box>
                              </Stack>
                            )}
                          </Box>

                          {/* Expanded detail */}
                          <Collapse in={expandedUserId === user.user_id} timeout="auto" unmountOnExit>
                            <Box sx={{ px: 3, py: 2.5, bgcolor: '#fafbfb', borderBottom: '1px solid #e1e3e5' }}>
                              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>User Details</Typography>
                              <Stack spacing={2}>
                                <Card sx={{ p: 2, borderRadius: '8px', border: '1px solid #e1e3e5', boxShadow: 'none' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                                    <Key sx={{ color: '#6d7175', fontSize: 18 }} />
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">OTP</Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', color: '#202223' }}>{formatOTP(user.otp)}</Typography>
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                                    <Email sx={{ color: '#6d7175', fontSize: 18 }} />
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">Email Address</Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-all' }}>{user.user_email || '-'}</Typography>
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Phone sx={{ color: '#6d7175', fontSize: 18 }} />
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">Contact Number</Typography>
                                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>{user.user_contact || '-'}</Typography>
                                    </Box>
                                  </Box>
                                </Card>
                                {(user.subscription_date || user.expiry_date) && (
                                  <Card sx={{ p: 2, borderRadius: '8px', border: '1px solid #e1e3e5', boxShadow: 'none' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                                      <CalendarToday sx={{ color: '#6d7175', fontSize: 18 }} />
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">Subscription Date</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDate(user.subscription_date)}</Typography>
                                      </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                      <CalendarToday sx={{ color: '#6d7175', fontSize: 18 }} />
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">Expiry Date</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDate(user.expiry_date)}</Typography>
                                      </Box>
                                    </Box>
                                  </Card>
                                )}
                              </Stack>
                            </Box>
                          </Collapse>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
              {/* end actual scroll container */}
            </Box>
            {/* end scroll wrapper */}

            {/* Frozen: Pagination */}
            {totalPages > 1 && !isLoading && filteredUsers.length > usersPerPage && (
              <Box sx={{ flexShrink: 0, display: 'flex', justifyContent: 'center', py: 2, borderTop: '1px solid #e1e3e5', bgcolor: 'white' }}>
                <Pagination count={totalPages} page={currentPage} onChange={handleChangePage}
                  sx={{
                    '& .MuiPaginationItem-root': { borderRadius: '8px', fontWeight: 500 },
                    '& .Mui-selected': { bgcolor: '#202223 !important', color: 'white' },
                  }}
                />
              </Box>
            )}
          </Card>
        </Box>
      </Box>

      {/* Send Coupon Dialog */}
      <Dialog open={openSendCouponDialog} onClose={handleCloseSendCouponDialog} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#202223' }}>Send Coupon to Users</Typography>
            <IconButton onClick={handleCloseSendCouponDialog} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Select Coupon *</InputLabel>
              <Select value={selectedCoupon} label="Select Coupon *"
                onChange={e => { setSelectedCoupon(e.target.value as number); setUserCouponHistory({}); }}
                disabled={isLoadingCoupons || activeCoupons.length === 0}
              >
                {activeCoupons.map((c: Coupon) => (
                  <MenuItem key={c.id} value={c.id}>{getCouponText(c)}</MenuItem>
                ))}
              </Select>
              {isLoadingCoupons && <Typography variant="caption" color="text.secondary">Loading coupons...</Typography>}
              {!isLoadingCoupons && activeCoupons.length === 0 && (
                <Typography variant="caption" color="error">No active coupons. Please create one first.</Typography>
              )}
            </FormControl>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Select Users ({selectedUsers.length} selected)</Typography>
                <Button onClick={handleSelectAllUsers} sx={{ textTransform: 'none' }}>
                  {selectedUsers.length === filteredUsers.length ? 'Deselect All' : 'Select All'}
                </Button>
              </Box>
              {selectedCoupon && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Users can only receive this coupon once. Already-sent users will be skipped.
                </Alert>
              )}
              <Card sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e1e3e5', boxShadow: 'none' }}>
                <List dense>
                  {filteredUsers.map((user: User) => {
                    const received = hasUserReceivedCoupon(user.user_id);
                    return (
                      <ListItem key={user.user_id} button
                        onClick={() => !received && handleUserSelection(user.user_id)}
                        disabled={received} sx={{ opacity: received ? 0.6 : 1 }}
                      >
                        <ListItemIcon>
                          <Checkbox edge="start" checked={selectedUsers.includes(user.user_id)}
                            tabIndex={-1} disableRipple disabled={received}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {`${user.first_name || ''} ${user.last_name || ''}`.trim() || 'N/A'}
                              </Typography>
                              {received && (
                                <Chip label="Already Sent" size="small"
                                  sx={{ bgcolor: '#ffebee', color: '#c62828', fontWeight: 600, fontSize: '10px' }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="caption" color="text.secondary">{user.user_email}</Typography>
                              <Typography variant="caption" display="block" color="text.secondary">
                                ID: {user.user_id} | OTP: {formatOTP(user.otp)} | {user.user_contact}
                              </Typography>
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Chip label={user.member_type || 'None'} size="small"
                            sx={{ ...getMemberTypeColor(user.member_type), fontWeight: 600, fontSize: '10px' }}
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    );
                  })}
                </List>
              </Card>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={handleCloseSendCouponDialog} disabled={isSending}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#eeeeee', bgcolor: 'rgb(244, 67, 54)', '&:hover': { bgcolor: '#d32f2f' } }}
          >
            Close
          </Button>
          <Button onClick={handleCloseSendCouponDialog} disabled={isSending}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#202223', bgcolor: '#F78E12', '&:hover': { bgcolor: '#e07d0a' } }}
          >
            Cancel
          </Button>
          <Button onClick={handleSendCoupon} variant="contained"
            disabled={isSending || !selectedCoupon || selectedUsers.length === 0}
            startIcon={<Send />}
            sx={{ bgcolor: '#008060', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#006e52' } }}
          >
            {isSending
              ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CircularProgress size={18} color="inherit" /> Sending...</Box>
              : `Send to ${selectedUsers.length} User${selectedUsers.length !== 1 ? 's' : ''}`
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserList;